package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.CalendarMonth
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.EventBusy
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.bookingsStrings
import org.gymly.i18n.strings
import org.gymly.model.AdminBookingItem
import org.gymly.model.AdminSettingsPermissionModule
import org.gymly.model.AdminBookingStatus
import org.gymly.model.AdminBookingUpsert
import org.gymly.model.AdminClient
import org.gymly.model.AdminScheduleKind
import org.gymly.model.AdminScheduleSlot
import org.gymly.model.AdminVisitItem
import org.gymly.model.AdminVisitSource
import org.gymly.model.AdminVisitUpsert
import org.gymly.model.AdminWeekday
import org.gymly.model.AdminWorker
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.WeekRange
import org.gymly.util.formatIsoToDisplayDate
import org.gymly.util.todayIsoDate
import org.gymly.util.userMessage
import org.gymly.ui.components.IsoCalendarDialog
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

private const val manualBookingsEnabled = false

private enum class BookingsDateFilter(val label: String, val days: Int?) {
    Week("Por semana", null),
    Day("Por día", null),
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private enum class BookingsMode(val label: String) {
    Classes("Clases (reserva)"),
    Sessions("Sesiones (piso)"),
}

private enum class VisitSubjectFilter(val label: String) {
    All("Todos"),
    Clients("Clientes"),
    Staff("Personal"),
}

private sealed class BookingsDialog {
    data object Create : BookingsDialog()
    data object CreateVisit : BookingsDialog()
    data class Edit(val item: AdminBookingItem) : BookingsDialog()
    data class Detail(val item: AdminBookingItem) : BookingsDialog()
    data class Delete(val item: AdminBookingItem) : BookingsDialog()
    data class DeleteVisit(val item: AdminVisitItem) : BookingsDialog()
}

/** Qué acciones muestra la pantalla según los permisos de Asistencia. El servidor aplica la regla final. */
private class AttendanceAccess(val canWrite: Boolean, val canDelete: Boolean)

private val LocalAttendanceAccess = staticCompositionLocalOf { AttendanceAccess(canWrite = false, canDelete = false) }

@Composable
fun AdminBookingsScreen(permissions: Set<AdminSettingsPermissionModule> = emptySet()) {
    val access = AttendanceAccess(
        canWrite = permissions.any {
            it == AdminSettingsPermissionModule.ATTENDANCE_OPERATE || it == AdminSettingsPermissionModule.ATTENDANCE_APPROVE
        },
        canDelete = permissions.any {
                it == AdminSettingsPermissionModule.ATTENDANCE_DELETE ||
                it == AdminSettingsPermissionModule.ATTENDANCE_APPROVE
        },
    )
    CompositionLocalProvider(LocalAttendanceAccess provides access) {
        AdminBookingsContent()
    }
}

@Composable
private fun AdminBookingsContent() {
    val pendingApprovals = LocalPendingApprovalNotifier.current
    val scope = rememberCoroutineScope()
    val tb = bookingsStrings()
    var items by remember { mutableStateOf<List<AdminBookingItem>>(emptyList()) }
    var visits by remember { mutableStateOf<List<AdminVisitItem>>(emptyList()) }
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var workers by remember { mutableStateOf<List<AdminWorker>>(emptyList()) }
    var schedules by remember { mutableStateOf<List<AdminScheduleSlot>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(BookingsDateFilter.Week) }
    var weekOffset by remember { mutableStateOf(0) }
    var selectedDayIso by remember { mutableStateOf(todayIsoDate()) }
    var visitSubjectFilter by remember { mutableStateOf(VisitSubjectFilter.All) }
    var mode by remember { mutableStateOf(if (manualBookingsEnabled) BookingsMode.Classes else BookingsMode.Sessions) }
    var dialog by remember { mutableStateOf<BookingsDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listBookings()
                visits = AdminRepo.api.listVisits()
                clients = AdminRepo.api.listClients()
                workers = runCatching { AdminRepo.api.listWorkers() }.getOrDefault(emptyList())
                schedules = AdminRepo.api.listSchedules()
            } catch (e: Exception) {
                error = e.userMessage(tb.loadFailed)
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val week = remember(weekOffset) { WeekRange.ofOffset(weekOffset) }
    val bookableSchedules = remember(schedules) {
        schedules.filter { it.kind == AdminScheduleKind.BOOKABLE_CLASS }
    }
    val openFloorSchedules = remember(schedules) {
        schedules.filter { it.kind == AdminScheduleKind.OPEN_FLOOR }
    }

    val filtered = remember(items, query, dateFilter, week, selectedDayIso) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        items.filter { b ->
            val haystack = listOf(
                b.clientName,
                b.franjaLabel(),
                b.status.labelEs(),
                b.sessionDate,
            ).joinToString(" ").lowercase()
            val matchesQuery = q.isEmpty() || haystack.contains(q)
            val matchesDate = when (dateFilter) {
                BookingsDateFilter.Week -> week.containsIsoDate(b.sessionDate)
                BookingsDateFilter.Day -> b.sessionDate.take(10) == selectedDayIso
                BookingsDateFilter.All -> true
                else -> {
                    val created = b.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                    if (created != null && cutoff != null) created >= cutoff else true
                }
            }
            matchesQuery && matchesDate
        }
    }

    val filteredVisits = remember(visits, query, dateFilter, week, selectedDayIso, visitSubjectFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        visits.filter { v ->
            val haystack = listOf(
                v.clientName.orEmpty(),
                v.staffName.orEmpty(),
                v.subjectName,
                v.scheduleTitle.orEmpty(),
                v.trainerName.orEmpty(),
                v.source.labelEs(),
                v.visitDate,
                if (v.isStaff) "personal trabajador" else "cliente socio",
            ).joinToString(" ").lowercase()
            val matchesQuery = q.isEmpty() || haystack.contains(q)
            val matchesSubject = when (visitSubjectFilter) {
                VisitSubjectFilter.All -> true
                VisitSubjectFilter.Clients -> !v.isStaff
                VisitSubjectFilter.Staff -> v.isStaff
            }
            val matchesDate = when (dateFilter) {
                BookingsDateFilter.Week -> week.containsIsoDate(v.visitDate)
                BookingsDateFilter.Day -> v.visitDate.take(10) == selectedDayIso
                BookingsDateFilter.All -> true
                else -> {
                    val created = v.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                        ?: runCatching { Instant.parse(v.occurredAt) }.getOrNull()
                    if (created != null && cutoff != null) created >= cutoff else true
                }
            }
            matchesQuery && matchesSubject && matchesDate
        }
    }

    val bookingsPaging = rememberAdminListPaging(
        initialSortKey = "client",
        resetKey = listOf(query, dateFilter, weekOffset, selectedDayIso),
    )
    val bookingPageItems = remember(filtered, bookingsPaging.sort, bookingsPaging.page) {
        filtered.adminSortedPaged(bookingsPaging.sort, bookingsPaging.page) { key ->
            when (key) {
                "status" -> { b -> b.status.name }
                "date" -> { b -> b.sessionDate }
                else -> { b -> b.clientName.lowercase() }
            }
        }
    }

    val visitsPaging = rememberAdminListPaging(
        initialSortKey = "date",
        initialDir = AdminSortDir.Desc,
        resetKey = listOf(query, dateFilter, weekOffset, selectedDayIso, visitSubjectFilter),
    )
    val visitPageItems = remember(filteredVisits, visitsPaging.sort, visitsPaging.page) {
        filteredVisits.adminSortedPaged(visitsPaging.sort, visitsPaging.page) { key ->
            when (key) {
                "source" -> { v -> v.source.name }
                "client" -> { v -> v.clientName.orEmpty().lowercase() }
                "staff" -> { v -> v.staffName.orEmpty().lowercase() }
                else -> { v -> v.visitDate }
            }
        }
    }

    val total = if (mode == BookingsMode.Classes) filtered.size else filteredVisits.size
    val reserved = filtered.count { it.status == AdminBookingStatus.CONFIRMED }
    val attended = filtered.count { it.status == AdminBookingStatus.ATTENDED }
    val cancelled = filtered.count { it.status.isCancelledBucket() }
    val clientVisits = filteredVisits.count { !it.isStaff }
    val staffVisits = filteredVisits.count { it.isStaff }
    val accessVisits = filteredVisits.count { it.source == AdminVisitSource.ACCESS }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        // Celular: la página se desplaza y el listado tiene altura fija, así no queda en cero.
        Column(Modifier.fillMaxSize()) {
            BookingsHeader(
                dateFilter = dateFilter,
                week = week,
                selectedDayIso = selectedDayIso,
                onFilterChange = { dateFilter = it },
                onWeekOffsetChange = { weekOffset = it },
                onSelectedDayChange = { selectedDayIso = it },
                compact = compact,
            )
            Spacer(Modifier.height(12.dp))
            if (manualBookingsEnabled) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    BookingsMode.entries.forEach { m ->
                        val selected = mode == m
                        OutlinedButton(
                            onClick = { mode = m },
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(
                                m.label,
                                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                                color = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                            )
                        }
                    }
                }
                Spacer(Modifier.height(16.dp))
            }
            if (mode == BookingsMode.Classes) {
                BookingsKpiRow(total, reserved, attended, cancelled, compact)
            } else {
                BookingsKpiRowSessions(total, clientVisits, staffVisits, accessVisits, compact)
            }
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            if (mode == BookingsMode.Classes) {
                BookingsDirectoryCard(
                    bookings = bookingPageItems,
                    totalCount = filtered.size,
                    listPaging = bookingsPaging,
                    loading = loading,
                    query = query,
                    onQueryChange = { query = it },
                    compact = compact,
                    showCreateButton = manualBookingsEnabled,
                    onCreate = { dialog = BookingsDialog.Create },
                    onAttend = { item ->
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.markBookingAttended(item.id)
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.markAttendedFailed)
                            } finally {
                                saving = false
                            }
                        }
                    },
                    onNoShow = { item ->
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.markBookingNoShow(item.id)
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.markNoShowFailed)
                            } finally {
                                saving = false
                            }
                        }
                    },
                    onView = { dialog = BookingsDialog.Detail(it) },
                    onEdit = { dialog = BookingsDialog.Edit(it) },
                    onDelete = { dialog = BookingsDialog.Delete(it) },
                )
            } else {
                VisitsDirectoryCard(
                    visits = visitPageItems,
                    totalCount = filteredVisits.size,
                    listPaging = visitsPaging,
                    loading = loading,
                    query = query,
                    onQueryChange = { query = it },
                    subjectFilter = visitSubjectFilter,
                    onSubjectFilterChange = { visitSubjectFilter = it },
                    compact = compact,
                    onCreate = { dialog = BookingsDialog.CreateVisit },
                    onDelete = { dialog = BookingsDialog.DeleteVisit(it) },
                )
            }
        }
    }

    when (val d = dialog) {
        is BookingsDialog.Detail -> BookingDetailDialog(d.item) { dialog = null }
        is BookingsDialog.Create -> BookingFormDialog(
            title = "Agregar reserva de clase",
            initial = null,
            clients = clients,
            schedules = bookableSchedules,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createBooking(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.bookingCreateFailed)
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is BookingsDialog.CreateVisit -> VisitFormDialog(
            clients = clients,
            workers = workers,
            schedules = openFloorSchedules,
            saving = saving,
            apiError = error,
            onDismiss = {
                if (!saving) {
                    error = null
                    dialog = null
                }
            },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createVisit(upsert)
                        dialog = null
                        mode = BookingsMode.Sessions
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.visitCreateFailed)
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is BookingsDialog.Edit -> BookingFormDialog(
            title = "Editar reserva",
            initial = d.item,
            clients = clients,
            schedules = bookableSchedules,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateBooking(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.bookingSaveFailed)
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is BookingsDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text(tb.deleteBookingTitle) },
            text = {
                Text(tb.deleteBookingConfirm(d.item.clientName))
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteBooking(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.bookingDeleteFailed)
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else strings().common.delete)
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text(strings().common.cancel, color = AdminColors.TextMuted)
                }
            },
        )
        is BookingsDialog.DeleteVisit -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text(tb.deleteVisitTitle) },
            text = { Text(tb.deleteVisitConfirm(d.item.subjectName, d.item.visitDate)) },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteVisit(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage(tb.visitDeleteFailed)
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else strings().common.delete)
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text(strings().common.cancel, color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun BookingsHeader(
    dateFilter: BookingsDateFilter,
    week: WeekRange,
    selectedDayIso: String,
    onFilterChange: (BookingsDateFilter) -> Unit,
    onWeekOffsetChange: (Int) -> Unit,
    onSelectedDayChange: (String) -> Unit,
    compact: Boolean = false,
) {
    var menuOpen by remember { mutableStateOf(false) }
    val controls: @Composable () -> Unit = {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            when (dateFilter) {
                BookingsDateFilter.Week -> WeekNavigator(week = week, onWeekOffsetChange = onWeekOffsetChange)
                BookingsDateFilter.Day -> DayNavigator(
                    selectedDayIso = selectedDayIso,
                    onSelectedDayChange = onSelectedDayChange,
                )
                else -> Unit
            }
            Box {
                OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                    Text(dateFilter.label, color = AdminColors.Text)
                }
                DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                    BookingsDateFilter.entries.forEach { opt ->
                        DropdownMenuItem(
                            text = { Text(opt.label) },
                            onClick = {
                                onFilterChange(opt)
                                menuOpen = false
                            },
                        )
                    }
                }
            }
        }
    }
    val title = if (manualBookingsEnabled) "Sesiones y reservas" else "Asistencia y sesiones"
    val subtitle = if (manualBookingsEnabled) {
        "Clases con reserva · sesiones de piso automáticas al pasar huella/rostro (socios y personal) o registro manual."
    } else {
        "Control de asistencia de socios y personal: entradas por terminal o registro manual. Filtra por día, semana, cliente o trabajador."
    }
    if (compact) {
        // Celular: solo título y controles (semana y filtro) en una fila; la descripción se omite para ganar espacio.
        Column(Modifier.fillMaxWidth()) {
            Text(title, fontSize = 22.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Spacer(Modifier.height(8.dp))
            controls()
        }
        return
    }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text(title, fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                subtitle,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        controls()
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DayNavigator(
    selectedDayIso: String,
    onSelectedDayChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var showPicker by remember { mutableStateOf(false) }
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        TextButton(onClick = { onSelectedDayChange(shiftIsoDate(selectedDayIso, -1)) }) {
            Text("‹", color = AdminColors.Primary, fontSize = 22.sp, fontWeight = FontWeight.Bold)
        }
        OutlinedButton(
            onClick = { showPicker = true },
            shape = RoundedCornerShape(10.dp),
        ) {
            Icon(
                Icons.Outlined.CalendarMonth,
                contentDescription = null,
                tint = AdminColors.Primary,
                modifier = Modifier.padding(end = 6.dp),
            )
            Text(
                dayFilterLabelEs(selectedDayIso),
                color = AdminColors.Text,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
                modifier = Modifier.padding(horizontal = 4.dp),
            )
        }
        TextButton(onClick = { onSelectedDayChange(shiftIsoDate(selectedDayIso, 1)) }) {
            Text("›", color = AdminColors.Primary, fontSize = 22.sp, fontWeight = FontWeight.Bold)
        }
    }
    if (showPicker) {
        IsoCalendarDialog(
            initialIso = selectedDayIso,
            onDismiss = { showPicker = false },
            onConfirm = { iso ->
                onSelectedDayChange(iso)
                showPicker = false
            },
            accent = AdminColors.Primary,
        )
    }
}

private fun shiftIsoDate(iso: String, deltaDays: Int): String {
    val base = iso.take(10).ifBlank { todayIsoDate() }
    val millis = runCatching {
        Instant.parse("${base}T00:00:00Z").toEpochMilliseconds()
    }.getOrElse {
        Instant.parse("${todayIsoDate()}T00:00:00Z").toEpochMilliseconds()
    }
    return Instant.fromEpochMilliseconds(millis + deltaDays * 86_400_000L).toString().take(10)
}

private fun dayFilterLabelEs(iso: String): String {
    val day = iso.take(10).ifBlank { todayIsoDate() }
    val display = formatIsoToDisplayDate(day)
    val today = todayIsoDate()
    return when (day) {
        today -> "Hoy · $display"
        shiftIsoDate(today, -1) -> "Ayer · $display"
        else -> display
    }
}

@Composable
private fun BookingsKpiRow(
    total: Int,
    reserved: Int,
    attended: Int,
    cancelled: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de reservas", total.toString(), AdminColors.Primary),
        Triple("Reservadas", reserved.toString(), Color(0xFF3B4CB8)),
        Triple("Asistidas", attended.toString(), Color(0xFF2E7D32)),
        Triple("Canceladas", cancelled.toString(), Color(0xFFE57373)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                BookingsKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun BookingsKpiRowSessions(
    total: Int,
    clients: Int,
    staff: Int,
    access: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de sesiones", total.toString(), AdminColors.Primary),
        Triple("Clientes", clients.toString(), Color(0xFF2E7D32)),
        Triple("Personal", staff.toString(), Color(0xFF3B4CB8)),
        Triple("Por acceso", access.toString(), Color(0xFF6A1B9A)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                BookingsKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun BookingsKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.BookingsDirectoryCard(
    bookings: List<AdminBookingItem>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    showCreateButton: Boolean,
    onCreate: () -> Unit,
    onAttend: (AdminBookingItem) -> Unit,
    onNoShow: (AdminBookingItem) -> Unit,
    onView: (AdminBookingItem) -> Unit,
    onEdit: (AdminBookingItem) -> Unit,
    onDelete: (AdminBookingItem) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Reservas", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                BookingsSearchField(query, onQueryChange)
                if (showCreateButton && LocalAttendanceAccess.current.canWrite) {
                    Button(
                        onClick = onCreate,
                        modifier = Modifier.fillMaxWidth().height(44.dp),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nueva reserva", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Reservas", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) { BookingsSearchField(query, onQueryChange) }
                    if (showCreateButton && LocalAttendanceAccess.current.canWrite) {
                        Button(
                            onClick = onCreate,
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text("+ Nueva reserva", fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay reservas todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    bookings.forEach { b ->
                        BookingMobileCard(b, onAttend, onNoShow, onView, onEdit, onDelete)
                    }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                BookingsTable(
                    bookings,
                    listPaging = listPaging,
                    onAttend,
                    onNoShow,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun BookingsSearchField(
    query: String,
    onQueryChange: (String) -> Unit,
    placeholder: String = "Buscar...",
) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun BookingsTable(
    bookings: List<AdminBookingItem>,
    listPaging: AdminListPaging,
    onAttend: (AdminBookingItem) -> Unit,
    onNoShow: (AdminBookingItem) -> Unit,
    onView: (AdminBookingItem) -> Unit,
    onEdit: (AdminBookingItem) -> Unit,
    onDelete: (AdminBookingItem) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Reserva", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.8f))
            AdminSortableHeader("Estado", "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminListHeaderLabel("Acciones", Modifier.width(220.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            bookings.forEach { b ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.8f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(b.status.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(b.clientName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(b.franjaLabel(), color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Box(Modifier.weight(0.9f)) { StatusBadge(b.status) }
                    Row(
                        Modifier.width(220.dp),
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (b.status.isReserved() && LocalAttendanceAccess.current.canWrite) {
                            AttendanceIconButton(
                                onClick = { onAttend(b) },
                                borderColor = Color(0xFF2E7D32),
                                contentColor = Color(0xFF2E7D32),
                                contentDescription = "Asistió",
                            ) {
                                Icon(Icons.Outlined.Add, null, tint = Color(0xFF2E7D32), modifier = Modifier.size(18.dp))
                            }
                            AttendanceIconButton(
                                onClick = { onNoShow(b) },
                                borderColor = Color(0xFFE57373),
                                contentColor = Color(0xFFE57373),
                                contentDescription = "No asistió",
                            ) {
                                Icon(Icons.Outlined.EventBusy, null, tint = Color(0xFFE57373), modifier = Modifier.size(18.dp))
                            }
                        }
                        IconButton(onClick = { onView(b) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (LocalAttendanceAccess.current.canWrite) IconButton(onClick = { onEdit(b) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                        }
                        if (LocalAttendanceAccess.current.canDelete) IconButton(onClick = { onDelete(b) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun AttendanceIconButton(
    onClick: () -> Unit,
    borderColor: Color,
    contentColor: Color,
    contentDescription: String,
    content: @Composable () -> Unit,
) {
    Box(
        Modifier
            .size(34.dp)
            .clip(RoundedCornerShape(8.dp))
            .border(1.5.dp, borderColor, RoundedCornerShape(8.dp))
            .background(Color.Transparent),
        contentAlignment = Alignment.Center,
    ) {
        IconButton(onClick = onClick) {
            content()
        }
    }
}

@Composable
private fun StatusBadge(status: AdminBookingStatus) {
    Text(
        status.labelEs(),
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(status.badgeBgArgb()))
            .padding(horizontal = 10.dp, vertical = 4.dp),
        color = Color(status.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
    )
}

@Composable
private fun BookingMobileCard(
    item: AdminBookingItem,
    onAttend: (AdminBookingItem) -> Unit,
    onNoShow: (AdminBookingItem) -> Unit,
    onView: (AdminBookingItem) -> Unit,
    onEdit: (AdminBookingItem) -> Unit,
    onDelete: (AdminBookingItem) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(Color(item.status.dotColorArgb())),
            )
            Spacer(Modifier.width(8.dp))
            Text(item.clientName, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            StatusBadge(item.status)
        }
        Spacer(Modifier.height(4.dp))
        Text(item.franjaLabel(), color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth(),
        ) {
            if (item.status.isReserved() && LocalAttendanceAccess.current.canWrite) {
                AttendanceIconButton(
                    onClick = { onAttend(item) },
                    borderColor = Color(0xFF2E7D32),
                    contentColor = Color(0xFF2E7D32),
                    contentDescription = "Asistió",
                ) {
                    Icon(Icons.Outlined.Add, null, tint = Color(0xFF2E7D32), modifier = Modifier.size(18.dp))
                }
                Spacer(Modifier.width(4.dp))
                AttendanceIconButton(
                    onClick = { onNoShow(item) },
                    borderColor = Color(0xFFE57373),
                    contentColor = Color(0xFFE57373),
                    contentDescription = "No asistió",
                ) {
                    Icon(Icons.Outlined.EventBusy, null, tint = Color(0xFFE57373), modifier = Modifier.size(18.dp))
                }
            }
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            if (LocalAttendanceAccess.current.canWrite) IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
            }
            if (LocalAttendanceAccess.current.canDelete) IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun BookingDetailDialog(item: AdminBookingItem, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Detalle de la reserva", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(8.dp))
            BookingDetailRow("Cliente", item.clientName)
            BookingDetailRow("Franja", item.franjaLabel())
            BookingDetailRow("Fecha", item.sessionDate)
            BookingDetailRow("Estado", item.status.labelEs())
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
private fun BookingDetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun BookingFormDialog(
    title: String,
    initial: AdminBookingItem?,
    clients: List<AdminClient>,
    schedules: List<AdminScheduleSlot>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminBookingUpsert) -> Unit,
) {
    val tb = bookingsStrings()
    var clientId by remember(initial) { mutableStateOf(initial?.clientId.orEmpty()) }
    var clientQuery by remember(initial) { mutableStateOf(initial?.clientName.orEmpty()) }
    var scheduleId by remember(initial) {
        mutableStateOf(initial?.scheduleId ?: schedules.firstOrNull()?.id.orEmpty())
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var scheduleMenu by remember { mutableStateOf(false) }

    val filteredClients = remember(clients, clientQuery) {
        val q = clientQuery.trim().lowercase()
        if (q.isEmpty()) clients.take(12)
        else clients.filter {
            it.fullName.lowercase().contains(q) ||
                (it.email?.lowercase()?.contains(q) == true)
        }.take(12)
    }
    val selectedSchedule = schedules.firstOrNull { it.id == scheduleId }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))

            Text("Cliente *", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedTextField(
                    value = clientQuery,
                    onValueChange = {
                        clientQuery = it
                        clientMenu = true
                        if (clientId.isNotEmpty()) {
                            val match = clients.firstOrNull { c -> c.id == clientId }
                            if (match == null || match.fullName != it) clientId = ""
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Buscar clientes...", color = AdminColors.TextMuted) },
                    leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                DropdownMenu(
                    expanded = clientMenu && filteredClients.isNotEmpty(),
                    onDismissRequest = { clientMenu = false },
                ) {
                    filteredClients.forEach { c ->
                        DropdownMenuItem(
                            text = { Text(c.fullName) },
                            onClick = {
                                clientId = c.id
                                clientQuery = c.fullName
                                clientMenu = false
                            },
                        )
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text("Franja", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { scheduleMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    val label = selectedSchedule?.let { scheduleFranjaLabel(it) } ?: "Seleccionar franja"
                    Text(label, color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                }
                DropdownMenu(expanded = scheduleMenu, onDismissRequest = { scheduleMenu = false }) {
                    if (schedules.isEmpty()) {
                        DropdownMenuItem(
                            text = { Text("No hay horarios") },
                            onClick = { scheduleMenu = false },
                        )
                    } else {
                        schedules.forEach { s ->
                            DropdownMenuItem(
                                text = { Text(scheduleFranjaLabel(s)) },
                                onClick = {
                                    scheduleId = s.id
                                    scheduleMenu = false
                                },
                            )
                        }
                    }
                }
            }

            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }

            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            ) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (clientId.isBlank()) {
                            formError = tb.errSelectClient
                            return@Button
                        }
                        if (scheduleId.isBlank()) {
                            formError = tb.errSelectSchedule
                            return@Button
                        }
                        onSave(
                            AdminBookingUpsert(
                                clientId = clientId,
                                scheduleId = scheduleId,
                                sessionDate = initial?.sessionDate,
                            ),
                        )
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

private fun scheduleFranjaLabel(s: AdminScheduleSlot): String {
    val day = AdminWeekday.fromIso(s.weekday).shortEn
    val time = s.startTime.take(5)
    return "$day $time — ${s.title}"
}

@Composable
private fun ColumnScope.VisitsDirectoryCard(
    visits: List<AdminVisitItem>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    subjectFilter: VisitSubjectFilter,
    onSubjectFilterChange: (VisitSubjectFilter) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onDelete: (AdminVisitItem) -> Unit,
) {
    val tb = bookingsStrings()
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                // Celular: título, botón de registrar y buscador arriba (siempre visibles); la descripción larga se omite.
                Text("Sesiones de piso", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(tb.visitRegisterButton, fontWeight = FontWeight.SemiBold)
                }
                BookingsSearchField(query, onQueryChange, placeholder = "Buscar cliente o trabajador…")
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    VisitSubjectFilter.entries.forEach { f ->
                        val selected = subjectFilter == f
                        OutlinedButton(
                            onClick = { onSubjectFilterChange(f) },
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(
                                f.label,
                                fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                                color = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                                fontSize = 12.sp,
                            )
                        }
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column {
                    Text("Sesiones de piso", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                    Text(
                        "Se crean solas al pasar huella/rostro en el Hikvision (socios y personal).",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 260.dp)) {
                        BookingsSearchField(query, onQueryChange, placeholder = "Buscar cliente o trabajador…")
                    }
                    Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(tb.visitRegisterButton, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
            Spacer(Modifier.height(10.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                VisitSubjectFilter.entries.forEach { f ->
                    val selected = subjectFilter == f
                    OutlinedButton(
                        onClick = { onSubjectFilterChange(f) },
                        shape = RoundedCornerShape(10.dp),
                    ) {
                        Text(
                            f.label,
                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
                            color = if (selected) AdminColors.Primary else AdminColors.TextMuted,
                        )
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text(
                    "No hay sesiones en este periodo. Al pasar el rostro/huella (socio o personal) " +
                        "debe aparecer aquí automáticamente (origen «Acceso»). Prueba «Simular entrada» en Configuración → Acceso.",
                    color = AdminColors.TextMuted,
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                if (!compact) {
                    Row(
                        Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        AdminSortableHeader("Persona", "client", listPaging.sort, listPaging::onSort, Modifier.weight(1.2f))
                        AdminSortableHeader("Tipo", "staff", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
                        AdminSortableHeader("Fecha", "date", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                        AdminSortableHeader("Origen", "source", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
                        Spacer(Modifier.width(40.dp))
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
                    Spacer(Modifier.height(8.dp))
                }
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    visits.forEach { v ->
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(12.dp))
                                .border(1.dp, AdminColors.Outline.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
                                .padding(12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            if (compact) {
                                Column(Modifier.weight(1f)) {
                                    Text(
                                        "${if (v.isStaff) "Personal" else "Cliente"} · ${v.subjectName}",
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                    Text(
                                        listOfNotNull(
                                            v.visitDate,
                                            v.source.labelEs(),
                                            v.trainerName,
                                            v.scheduleTitle,
                                        ).joinToString(" · "),
                                        color = AdminColors.TextMuted,
                                        fontSize = 12.sp,
                                    )
                                }
                            } else {
                                Text(
                                    v.subjectName.ifBlank { "—" },
                                    Modifier.weight(1.2f),
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 13.sp,
                                )
                                Text(
                                    if (v.isStaff) "Personal" else "Cliente",
                                    Modifier.weight(0.7f),
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 13.sp,
                                    color = if (v.isStaff) AdminColors.Primary else Color(0xFF2E7D32),
                                )
                                Text(v.visitDate, Modifier.weight(0.8f), fontSize = 13.sp)
                                Text(v.source.labelEs(), Modifier.weight(0.8f), fontSize = 13.sp, color = AdminColors.TextMuted)
                            }
                            IconButton(onClick = { onDelete(v) }) {
                                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                            }
                        }
                    }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun VisitFormDialog(
    clients: List<AdminClient>,
    workers: List<AdminWorker>,
    schedules: List<AdminScheduleSlot>,
    saving: Boolean,
    apiError: String? = null,
    onDismiss: () -> Unit,
    onSave: (AdminVisitUpsert) -> Unit,
) {
    val tb = bookingsStrings()
    var subjectIsStaff by remember { mutableStateOf(false) }
    var clientId by remember { mutableStateOf("") }
    var clientQuery by remember { mutableStateOf("") }
    var staffId by remember { mutableStateOf("") }
    var staffQuery by remember { mutableStateOf("") }
    var scheduleId by remember { mutableStateOf(schedules.firstOrNull()?.id.orEmpty()) }
    var formError by remember { mutableStateOf<String?>(null) }
    var clientMenu by remember { mutableStateOf(false) }
    var staffMenu by remember { mutableStateOf(false) }
    var scheduleMenu by remember { mutableStateOf(false) }
    val displayError = formError ?: apiError

    val filteredClients = remember(clients, clientQuery) {
        val q = clientQuery.trim().lowercase()
        if (q.isEmpty()) clients.take(12)
        else clients.filter {
            it.fullName.lowercase().contains(q) || (it.email?.lowercase()?.contains(q) == true)
        }.take(12)
    }
    val filteredWorkers = remember(workers, staffQuery) {
        val q = staffQuery.trim().lowercase()
        val active = workers.filter { it.active }
        if (q.isEmpty()) active.take(12)
        else active.filter {
            it.displayName.lowercase().contains(q) || (it.email?.lowercase()?.contains(q) == true)
        }.take(12)
    }
    val selectedSchedule = schedules.firstOrNull { it.id == scheduleId }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        Column(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(tb.visitRegisterTitle, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Text(
                tb.visitRegisterHint,
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 12.dp),
            )

            Text("Tipo", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(6.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(
                    onClick = {
                        subjectIsStaff = false
                        staffId = ""
                        staffQuery = ""
                        formError = null
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(
                        "Cliente",
                        fontWeight = if (!subjectIsStaff) FontWeight.SemiBold else FontWeight.Normal,
                        color = if (!subjectIsStaff) AdminColors.Primary else AdminColors.TextMuted,
                    )
                }
                OutlinedButton(
                    onClick = {
                        subjectIsStaff = true
                        clientId = ""
                        clientQuery = ""
                        scheduleId = ""
                        formError = null
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(
                        "Personal",
                        fontWeight = if (subjectIsStaff) FontWeight.SemiBold else FontWeight.Normal,
                        color = if (subjectIsStaff) AdminColors.Primary else AdminColors.TextMuted,
                    )
                }
            }
            Spacer(Modifier.height(12.dp))

            if (subjectIsStaff) {
                Text("Trabajador *", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                Box {
                    OutlinedTextField(
                        value = staffQuery,
                        onValueChange = {
                            staffQuery = it
                            staffMenu = true
                            if (staffId.isNotEmpty()) {
                                val match = workers.firstOrNull { w -> w.id == staffId }
                                if (match == null || match.displayName != it) staffId = ""
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !saving,
                        placeholder = { Text("Buscar trabajadores…", color = AdminColors.TextMuted) },
                        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    DropdownMenu(
                        expanded = staffMenu && filteredWorkers.isNotEmpty(),
                        onDismissRequest = { staffMenu = false },
                    ) {
                        filteredWorkers.forEach { w ->
                            DropdownMenuItem(
                                text = { Text(w.displayName) },
                                onClick = {
                                    staffId = w.id
                                    staffQuery = w.displayName
                                    staffMenu = false
                                },
                            )
                        }
                    }
                }
            } else {
                Text("Cliente *", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                Box {
                    OutlinedTextField(
                        value = clientQuery,
                        onValueChange = {
                            clientQuery = it
                            clientMenu = true
                            if (clientId.isNotEmpty()) {
                                val match = clients.firstOrNull { c -> c.id == clientId }
                                if (match == null || match.fullName != it) clientId = ""
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        enabled = !saving,
                        placeholder = { Text("Buscar clientes…", color = AdminColors.TextMuted) },
                        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    DropdownMenu(
                        expanded = clientMenu && filteredClients.isNotEmpty(),
                        onDismissRequest = { clientMenu = false },
                    ) {
                        filteredClients.forEach { c ->
                            DropdownMenuItem(
                                text = { Text(c.fullName) },
                                onClick = {
                                    clientId = c.id
                                    clientQuery = c.fullName
                                    clientMenu = false
                                },
                            )
                        }
                    }
                }
                Spacer(Modifier.height(12.dp))
                Text("Franja de piso (opcional)", color = AdminColors.TextMuted, fontSize = 12.sp)
                Spacer(Modifier.height(4.dp))
                if (schedules.isEmpty()) {
                    Text(
                        "No hay franjas «Piso / entrenador». Se registrará la visita sin entrenador, o créalas en Horarios.",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                } else {
                    Box {
                        OutlinedButton(
                            onClick = { scheduleMenu = true },
                            enabled = !saving,
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(10.dp),
                        ) {
                            Text(
                                selectedSchedule?.let { scheduleFranjaLabel(it) } ?: "Sin franja (solo check-in)",
                                color = AdminColors.Text,
                                modifier = Modifier.fillMaxWidth(),
                            )
                        }
                        DropdownMenu(expanded = scheduleMenu, onDismissRequest = { scheduleMenu = false }) {
                            DropdownMenuItem(
                                text = { Text("Sin franja") },
                                onClick = {
                                    scheduleId = ""
                                    scheduleMenu = false
                                },
                            )
                            schedules.forEach { s ->
                                DropdownMenuItem(
                                    text = { Text(scheduleFranjaLabel(s)) },
                                    onClick = {
                                        scheduleId = s.id
                                        scheduleMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
            }

            displayError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }

            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            ) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (subjectIsStaff) {
                            if (staffId.isBlank()) {
                                formError = tb.errSelectWorker
                                return@Button
                            }
                            onSave(AdminVisitUpsert(staffId = staffId))
                        } else {
                            if (clientId.isBlank()) {
                                formError = tb.errSelectClient
                                return@Button
                            }
                            onSave(
                                AdminVisitUpsert(
                                    clientId = clientId,
                                    scheduleId = scheduleId.ifBlank { null },
                                    trainerId = selectedSchedule?.trainerId,
                                ),
                            )
                        }
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Registrar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}
